import {
  SEED_COMPANIES,
  SEED_USERS,
  SEED_WORKERS,
  SEED_ZONES,
  SEED_PROJECTS,
  SEED_DEVICES,
  SEED_ALARM_CONFIGS,
  SEED_CONFIGURATIONS,
} from '../../../seeder/index';

export const MOCK_DB_STORAGE_KEY = 'jimm.mock-db.v1';

// Deep-clone seed data so mutations during a session don't corrupt the originals
function clone<T>(arr: T[]): T[] {
  return arr.map(item => ({ ...(item as object) })) as T[];
}

export class MockDb {
  companies    = clone(SEED_COMPANIES);
  users        = clone(SEED_USERS);
  workers      = clone(SEED_WORKERS);
  zones        = clone(SEED_ZONES);
  projects     = clone(SEED_PROJECTS);
  devices      = clone(SEED_DEVICES);
  alarmConfigs = clone(SEED_ALARM_CONFIGS);
  configurations = clone(SEED_CONFIGURATIONS);
  notificationEmails: any[] = [];
  notificationContacts: any[] = [];
  deviceActivities: any[] = [
    {
      id: 1, device_id: 1, actor_user_id: 2, actor_name: 'María García',
      occurred_at: '2026-10-09T09:15:00.000Z', field: 'device_created',
      old_value: null, new_value: 'JIM-001',
    },
    {
      id: 2, device_id: 1, actor_user_id: 2, actor_name: 'María García',
      occurred_at: '2026-10-09T09:20:00.000Z', field: 'primary_worker',
      old_value: null, new_value: 'Juan Martínez',
    },
  ];

  constructor() {
    this.restore();
  }

  private restore(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const saved = JSON.parse(localStorage.getItem(MOCK_DB_STORAGE_KEY) || 'null');
      if (!saved) return;
      for (const key of [
        'companies', 'users', 'workers', 'zones', 'projects', 'devices', 'alarmConfigs',
        'configurations', 'notificationEmails', 'notificationContacts', 'deviceActivities',
      ]) {
        if (Array.isArray(saved[key])) (this as any)[key] = saved[key];
      }
    } catch {
      localStorage.removeItem(MOCK_DB_STORAGE_KEY);
    }
  }

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(MOCK_DB_STORAGE_KEY, JSON.stringify({
      companies: this.companies,
      users: this.users,
      workers: this.workers,
      zones: this.zones,
      projects: this.projects,
      devices: this.devices,
      alarmConfigs: this.alarmConfigs,
      configurations: this.configurations,
      notificationEmails: this.notificationEmails,
      notificationContacts: this.notificationContacts,
      deviceActivities: this.deviceActivities,
    }));
  }

  // ── Generic helpers ────────────────────────────────────────────────────────

  findById<T extends { id: number }>(arr: T[], id: number): T | undefined {
    return arr.find(i => i.id === id);
  }

  nextId<T extends { id: number }>(arr: T[]): number {
    return arr.length ? Math.max(...arr.map(i => i.id)) + 1 : 1;
  }

  add<T extends { id: number }>(arr: T[], item: Partial<T>): T {
    const newItem = { ...item, id: this.nextId(arr) } as T;
    arr.push(newItem);
    this.persist();
    return newItem;
  }

  update<T extends { id: number }>(arr: T[], id: number, patch: Partial<T>): T | null {
    const idx = arr.findIndex(i => i.id === id);
    if (idx === -1) return null;
    arr[idx] = { ...arr[idx], ...patch, id };
    this.persist();
    return arr[idx];
  }

  remove<T extends { id: number }>(arr: T[], id: number): boolean {
    const idx = arr.findIndex(i => i.id === id);
    if (idx === -1) return false;
    arr.splice(idx, 1);
    this.persist();
    return true;
  }

  // ── Alarm config helpers (keyed by parent_type + parent_id) ───────────────

  findAlarmConfig(parentType: string, parentId: number): any {
    return this.alarmConfigs.find(
      c => c.parent_type === parentType && c.parent_id === parentId
    );
  }

  upsertAlarmConfig(patch: any): any {
    const idx = this.alarmConfigs.findIndex(
      c => c.parent_type === patch.parent_type && c.parent_id === patch.parent_id
    );
    if (idx === -1) {
      this.alarmConfigs.push(patch);
      this.persist();
      return patch;
    }
    this.alarmConfigs[idx] = { ...this.alarmConfigs[idx], ...patch };
    this.persist();
    return this.alarmConfigs[idx];
  }

  removeAlarmConfig(parentType: string, parentId: number): boolean {
    const idx = this.alarmConfigs.findIndex(
      c => c.parent_type === parentType && c.parent_id === parentId
    );
    if (idx === -1) return false;
    this.alarmConfigs.splice(idx, 1);
    this.persist();
    return true;
  }

  // ── Device configuration ───────────────────────────────────────────────────

  findConfig(companyId: number, deviceId: number): any {
    return this.configurations.find(
      c => c.company_id === companyId && c.device_id === deviceId
    );
  }

  upsertConfig(companyId: number, deviceId: number, patch: any): any {
    const idx = this.configurations.findIndex(
      c => c.company_id === companyId && c.device_id === deviceId
    );
    if (idx === -1) {
      const newCfg = { ...patch, company_id: companyId, device_id: deviceId };
      this.configurations.push(newCfg);
      this.persist();
      return newCfg;
    }
    this.configurations[idx] = { ...this.configurations[idx], ...patch };
    this.persist();
    return this.configurations[idx];
  }
}

// Singleton instance – shared across the whole mock session
export const mockDb = new MockDb();
